import { useEffect, useState } from 'react';
import { fetchHotelRooms, fetchRoomPriceCalendar } from '../api';
import { Icons, LocalizedTotalPrice, SearchParams, YadoHotel, YadoRoom } from '../types';
import RoomPlanList from './RoomList';
import SearchForm from '../components/SearchForm';
import { formatQuery, parseUrlParamsToQuery } from '../utils';
import { addDays, startOfDay } from 'date-fns';
import i18n from "../../i18n";

export type HotelPlansSearchProps = {
  yadoId: string;
  hotel: YadoHotel;
  icons: Icons;
  reservableFrom: string;
  reservableTo: string;
  minimumStayDays: number;
  minimumClosingDays: number;
  nearestResortCode: string;
};

const HotelPlansSearch: React.FC<HotelPlansSearchProps> = ({ yadoId, hotel, icons, reservableFrom, reservableTo, minimumStayDays, minimumClosingDays, nearestResortCode }) => {
  const searchParams = new URLSearchParams(window.location.search);
  const forStg = searchParams.get('for_stg') === 'true';
  const today = new Date();
  const fourtyFiveDaysLater = startOfDay(addDays(today, 45)); // 45日後
  const reservableFromDate = new Date(reservableFrom); // プラン予約開始設定日
  const reservableToDate = new Date(reservableTo);  // プラン予約終了設定日
  const openingDate = addDays(new Date(), minimumClosingDays); // 手仕舞いされていない最初の日
  const minDate = openingDate > reservableFromDate ? startOfDay(openingDate) : reservableFromDate;
  const defaultCheckinDate = fourtyFiveDaysLater > minDate ? fourtyFiveDaysLater : minDate;
  const defaultCheckoutDate = addDays(defaultCheckinDate, minimumStayDays);

  const defaultQuery: SearchParams = {
    checkinDate: defaultCheckinDate,
    checkoutDate: defaultCheckoutDate,
    people: { adult: 2, children: [] },
    areaCodes: [],
    resortCodes: [],
    skiInOut: null,
    hotSpring: null,
    forStg: forStg,
  };
  const initialQuery = parseUrlParamsToQuery(window.location.search, defaultQuery);

  const [rooms, setRooms] = useState<YadoRoom[]>([]);
  const [priceCalendar, setPriceCalendar] = useState<{ date: string, price: LocalizedTotalPrice }[]>([]);
  const [query, setQuery] = useState<SearchParams>(initialQuery);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchInitiated, setSearchInitiated] = useState<boolean>(false);
  const [committedQuery, setCommittedQuery] = useState<SearchParams>(query);

  useEffect(() => {
    updateRooms();
    updateRoomPriceCalendar();
  }, []);

  const updateRooms = async () => {
    setIsLoading(true);
    setSearchInitiated(true);
    const formattedQuery = formatQuery(query);
    await fetchHotelRooms(yadoId, formattedQuery).then((response) => {
      setRooms(response || []);
      setCommittedQuery(query);
      setIsLoading(false);
    }).catch(() => {
      setIsLoading(false);
    });
  };

  const updateRoomPriceCalendar = async () => {
    const formattedQuery = formatQuery(query);
    await fetchRoomPriceCalendar(yadoId, formattedQuery).then((response) => {
      setPriceCalendar(response);
    });
  };

  return (
    <div className="container px-0">
      {!forStg && (
        <div className="row mx-0 align-items-center justify-content-between p-3 mb-4 rounded" style={{ backgroundColor: '#E6F3FF' }}>
          <SearchForm query={query} minDate={minDate} maxDate={reservableToDate} priceCalendar={priceCalendar} handleQueryChange={(newQuery) => setQuery(newQuery)} minimumStayDays={minimumStayDays} />
          <button
            type='submit'
            onClick={updateRooms}
            className="col-12 col-md-3 mt-2 mt-md-0 btn btn-primary rounded-pill font-weight-bold"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                <span className="sr-only">Loading...</span>
              </>
            ) : (
              i18n.t('components.accommodation.search_form.search')
            )}
          </button>
        </div>
      )}

      {searchInitiated && !isLoading && rooms.length === 0 && (
        <p className="text-center text-muted">
          {i18n.t('stay_packages.hotels.index.no_rooms_that_match_your_search_criteria')}
          <br />
          {i18n.t('stay_packages.hotels.index.please_modify_your_search_criteria')}
        </p>
      )}

      {rooms.length > 0 && (
        <RoomPlanList query={committedQuery} hotel={hotel} rooms={rooms} icons={icons} nearestResortCode={nearestResortCode} />
      )}
    </div>
  );
};

export default HotelPlansSearch;
